import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from "file-saver";
import { frenchDateOnly } from "../../../variables/functions";
import { displayFloorNum, displayNum } from "../../../variables/numbersFunctions";
import entete from "../../../assets/img/entete_pool.png";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";

// Libellé du statut à partir du code StatutDossier (cf. backend mapEtat) — identique
// à l'affichage du tableau pour que PDF/Excel et écran soient cohérents.
const ETAT_LABELS = {
    A: "À valider", I: "En instance", O: "Ouvert", T: "Terminé",
    S: "Sans suite", R: "Réouvert", Ta: "Tardif", F: "Forclose", Rf: "Refusé",
};
const etatLabel = (code) => ETAT_LABELS[code] || code || "-";

// ============================================================================
// Habillage PDF moderne (entête Pool + pied avec logos des compagnies)
// ============================================================================
export const NAVY = [36, 33, 102];
let _enteteCache;
let _logosCache;

// Charge une image (URL/dataURL) → { url(dataURL), ratio } via un canvas.
const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        try {
            const c = document.createElement("canvas");
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext("2d").drawImage(img, 0, 0);
            resolve({ url: c.toDataURL("image/png"), ratio: img.naturalWidth / Math.max(1, img.naturalHeight) });
        } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
});

export const loadEntete = async () => {
    if (_enteteCache !== undefined) return _enteteCache;
    _enteteCache = await loadImage(entete);
    return _enteteCache;
};

// Logos des compagnies membres (base64 fournis par l'API).
export const loadLogos = async () => {
    if (_logosCache !== undefined) return _logosCache;
    try {
        const { data } = await axiosInstance.get("/api/organisme/all/assurances-actives");
        const list = await Promise.all((data || [])
            .filter((i) => i.logoDisplay)
            .map((i) => loadImage(`data:image/png;base64,${i.logoDisplay}`)));
        _logosCache = list.filter(Boolean);
    } catch (e) { _logosCache = []; }
    return _logosCache;
};

// Logo de la COMPAGNIE de l'utilisateur connecté, au format attendu par addImage.
// Renvoie null pour un utilisateur du Pool (pas de compagnie propre) → l'entête retombe
// alors sur la mise en page historique (Pool à gauche).
let _logoCompagnieCache;
export const loadLogoCompagnieConnectee = async () => {
    if (_logoCompagnieCache !== undefined) return _logoCompagnieCache;
    try {
        const org = LOGGED_USER?.profil?.organisme;
        if (!org?.code || org?.typeOrganisme === "POOL") {
            _logoCompagnieCache = null;
            return null;
        }
        const response = await getCompagnieLogo(org.code);
        const bytes = await response.arrayBuffer();
        const blob = new Blob([bytes], { type: "image/png" });
        const objectUrl = URL.createObjectURL(blob);
        // loadImage repasse par un canvas → data URL, seul format accepté par jsPDF.
        _logoCompagnieCache = await loadImage(objectUrl);
        URL.revokeObjectURL(objectUrl);
    } catch (e) {
        _logoCompagnieCache = null;
    }
    return _logoCompagnieCache;
};

// Nombre TOTAL de pages : placeholder résolu APRÈS la génération (finalizeEtatPdf).
// didDrawPage est appelé PENDANT la construction → getNumberOfPages() y vaut le
// nombre de pages créées jusque-là, d'où l'ancien « 1/1, 2/2, 3/3… ».
export const TOTAL_PAGES_EXP = "{nb_pages}";
// Tri commun des EXPORTS : dates de la plus ANCIENNE à la plus récente,
// quel que soit l'ordre servi par l'API (l'écran /validation-decaissement/v2
// liste du plus récent au plus ancien, /etats/sinistres l'inverse).
export const triExportParDateCroissante = (data, pick = (r) => r?.dateReglement || r?.dateSaisie || r?.createdAt) =>
    [...(data || [])].sort((a, b) => new Date(pick(a) || 0) - new Date(pick(b) || 0));

export const finalizeEtatPdf = (doc) => {
    if (typeof doc.putTotalPages === "function") {
        doc.putTotalPages(TOTAL_PAGES_EXP);
    }
    return doc;
};

// Contexte « organisme/compagnie sélectionné(e) » commun à tous les états, posé par l'écran juste
// avant la génération (setEtatCompagnie). Injecté automatiquement dans le sous-titre PDF et en
// tête des exports Excel, sans avoir à threader le libellé dans chaque générateur.
let _etatCompagnie = null;
export const setEtatCompagnie = (label) => { _etatCompagnie = (label && `${label}`.trim()) || null; };
// Lecture LIVE du contexte compagnie (pour les générateurs qui n'utilisent pas drawEtatHeaderFooter,
// ex. l'export CIMA de generatesHook).
export const getEtatCompagnie = () => _etatCompagnie;
// Lignes d'en-tête Excel (compagnie) — vide si aucune compagnie sélectionnée (rapports Pool-only).
export const etatCompagnieRows = () => (_etatCompagnie ? [[`Organisme / Compagnie : ${_etatCompagnie}`], [""]] : []);

// Contexte « État du sinistre » (filtre choisi sur l'écran), posé juste avant la génération
// (setEtatSinistreFiltre) — injecté au sous-titre PDF et en tête des exports Excel, au même titre que
// la catégorie et l'organisme. Vide quand « Tous les sinistres » (aucun filtre d'état).
let _etatSinistreLabel = null;
export const setEtatSinistreFiltre = (label) => { _etatSinistreLabel = (label && `${label}`.trim()) || null; };
export const getEtatSinistreFiltre = () => _etatSinistreLabel;
export const etatSinistreRows = () => (_etatSinistreLabel ? [[`État du sinistre : ${_etatSinistreLabel}`], [""]] : []);

// Libellé de période SÛR : `date` peut être null (aucune période choisie) → évite le crash `date[0]`
// de null (TypeError dans didDrawPage). Majuscules pour les titres.
const periodeTitre = (date) => (date && date[0] && date[1])
    ? `DU ${frenchDateOnly(date[0])} AU ${frenchDateOnly(date[1])}` : "TOUTES PÉRIODES";
const periodeFichier = (date) => (date && date[0] && date[1])
    ? `${frenchDateOnly(date[0])}_${frenchDateOnly(date[1])}` : "Toutes_periodes";

export const drawEtatHeaderFooter = (doc, { title, subtitle, enteteData, logos, formattedNow, logoCompagnie, footerContact }) => {
    // Ajoute « Organisme : … » au sous-titre de tous les états portant une sélection de compagnie.
    if (_etatCompagnie) {
        subtitle = subtitle ? `${subtitle}   ·   Organisme : ${_etatCompagnie}` : `Organisme : ${_etatCompagnie}`;
    }
    // Ajoute « État : … » (filtre État du sinistre choisi), au même titre que catégorie/organisme.
    if (_etatSinistreLabel) {
        subtitle = subtitle ? `${subtitle}   ·   État : ${_etatSinistreLabel}` : `État : ${_etatSinistreLabel}`;
    }
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

    // ── Entête ──
    // Deux mises en page :
    //  • logoCompagnie fourni (utilisateur d'une compagnie) → COMPAGNIE à gauche, POOL à droite,
    //    comme les Conditions Particulières ;
    //  • sinon (Pool, ou logo indisponible) → mise en page historique : Pool à gauche.
    const hLogo = 13;
    if (logoCompagnie?.url) {
        const wc = Math.min(45, hLogo * (logoCompagnie.ratio || 2));
        try { doc.addImage(logoCompagnie.url, "PNG", 14, 7, wc, hLogo); } catch (e) { /* noop */ }
        if (enteteData?.url) {
            const wp = Math.min(70, hLogo * (enteteData.ratio || 4));
            try { doc.addImage(enteteData.url, "PNG", pw - 14 - wp, 7, wp, hLogo); } catch (e) { /* noop */ }
        } else {
            doc.setFontSize(11).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.text("POOL TPVM-VT GIE", pw - 14, 14, { align: "right" });
        }
        // Le sous-titre passerait SOUS le logo Pool : on le descend et on le centre.
        if (subtitle) {
            doc.setFontSize(9).setFont("helvetica", "normal").setTextColor(90, 90, 90);
            doc.text(subtitle, pw / 2, 33, { align: "center" });
        }
    } else {
        // Bord droit du bloc « Pool », pour savoir où le sous-titre peut commencer.
        let finLogo = 14;
        if (enteteData?.url) {
            const w = Math.min(82, hLogo * (enteteData.ratio || 4));
            try { doc.addImage(enteteData.url, "PNG", 14, 7, w, hLogo); } catch (e) { /* noop */ }
            finLogo = 14 + w;
        } else {
            doc.setFontSize(13).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.text("POOL TPVM-VT GIE", 14, 14);
            finLogo = 14 + doc.getTextWidth("POOL TPVM-VT GIE");
        }
        if (subtitle) {
            // Le sous-titre partage la ligne du logo : il DOIT tenir dans la place restante.
            // Un sous-titre long (« Par caissier et par agence · du … au … · NOM ») venait
            // sinon buter contre le logo. On le renvoie à la ligne dans l'espace disponible
            // plutôt que de le descendre sous le titre : ici le tableau démarre à y≈32, le
            // descendre le ferait passer SOUS la première ligne de données.
            doc.setFontSize(9).setFont("helvetica", "normal").setTextColor(90, 90, 90);
            const dispo = pw - 14 - finLogo - 6;
            const lignes = doc.splitTextToSize(subtitle, Math.max(40, dispo));
            // Bloc centré verticalement sur la hauteur du logo, aligné à droite.
            const interligne = 4;
            let y = 13.5 - ((lignes.length - 1) * interligne) / 2;
            lignes.forEach((l) => { doc.text(l, pw - 14, y, { align: "right" }); y += interligne; });
        }
    }
    doc.setFontSize(12).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
    doc.text(title, pw / 2, 25, { align: "center" });
    doc.setDrawColor(NAVY[0], NAVY[1], NAVY[2]).setLineWidth(0.6);
    // Filet sous le sous-titre centré quand il est descendu (mise en page 2 logos).
    doc.line(14, logoCompagnie?.url ? 36 : 28.5, pw - 14, logoCompagnie?.url ? 36 : 28.5);

    // ── Pied : logos des compagnies ──
    if (logos?.length) {
        const lh = 6, gap = 5;
        const widths = logos.map((l) => lh * (l.ratio || 2));
        const total = widths.reduce((a, b) => a + b, 0) + gap * (logos.length - 1);
        const scale = total > (pw - 28) ? (pw - 28) / total : 1;
        let x = (pw - total * scale) / 2;
        const y = ph - 16;
        logos.forEach((l, i) => {
            const w = widths[i] * scale;
            try { doc.addImage(l.url, "PNG", x, y, w, lh * scale); } catch (e) { /* noop */ }
            x += w + gap * scale;
        });
    }
    if (footerContact) {
        // Pied « conditions particulières » : ligne contact + mention légale (au-dessus/au-dessous des
        // logos compagnies déjà dessinés), pour aligner le rapport sur le footer des CP (DocumentFooter).
        doc.setFontSize(7).setFont("helvetica", "normal").setTextColor(90, 90, 90);
        doc.text("Tél: +228 92-62-66-66   |   Whatsapp: +228 70-48-11-11   |   Télécopie: +228 22-20-55-80   |   N°RC: 2009 C 0771 - N°COE: 1000 16 1028",
            pw / 2, ph - 20, { align: "center" });
        doc.setFontSize(9).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text("Entreprises régies par le code des assurances", pw / 2, ph - 8.5, { align: "center" });
        doc.setFontSize(6.5).setFont("helvetica", "normal").setTextColor(140, 140, 140);
        doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, ph - 3.5);
        doc.text(`Page ${currentPage} / ${TOTAL_PAGES_EXP}`, pw - 14, ph - 3.5, { align: "right" });
    } else {
        doc.setDrawColor(210, 210, 210).setLineWidth(0.3);
        doc.line(14, ph - 8.5, pw - 14, ph - 8.5);
        doc.setFontSize(7.5).setFont("helvetica", "normal").setTextColor(120, 120, 120);
        doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, ph - 5);
        doc.text(`Page ${currentPage} / ${TOTAL_PAGES_EXP}`, pw - 14, ph - 5, { align: "right" });
    }
};

// Générateur commun aux états "sinistres déclarés / survenus".
const buildSinistresPdf = async ({ data, date, categorieCode, title }) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    // Colonnes : Évaluation d'OUVERTURE (statique, réintroduite à la demande) + Évaluation COURANTE
    // (celle qui reflète le dossier ; SAP = Évaluation − règlements). Cumul de règlement décomposé :
    // « Exercices antérieurs » (avant l'exercice) / « Paiement de l'exercice » (pendant) / « Cumul
    // règlements » (total). La colonne SAP est placée APRÈS le cumul total (reste à payer final).
    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Imm.", "Date Surv.", "Date Décl.",
        "Lieu", "Assuré", "Éval. Ouv.", "Éval. cour.",
        "Paiements exercices antérieurs", "Paiement de l'exercice", "Cumul des paiements", "SAP", "Statut",
    ];
    const rows = (data || []).map((item) => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item?.nom} ${item?.prenom}` : item?.nom || "",
        displayNum(item?.evaluationOuverture || 0),
        displayNum(item?.evaluation || 0),
        displayNum(item?.cumulReglementsAvant || 0),
        displayNum(item?.cumulReglementsExercice || 0),
        displayNum(item?.cumulReglements || 0),
        displayNum(item?.sap || 0),
        etatLabel(item?.etat),
    ]));

    // Ligne TOTAL : somme des colonnes chiffrées + nb de sinistres.
    const totaux = (data || []).reduce((a, it) => ({
        evOuv: a.evOuv + (Number(it?.evaluationOuverture) || 0),
        ev: a.ev + (Number(it?.evaluation) || 0),
        sap: a.sap + (Number(it?.sap) || 0),
        crAvant: a.crAvant + (Number(it?.cumulReglementsAvant) || 0),
        crExerc: a.crExerc + (Number(it?.cumulReglementsExercice) || 0),
        cr: a.cr + (Number(it?.cumulReglements) || 0),
    }), { evOuv: 0, ev: 0, sap: 0, crAvant: 0, crExerc: 0, cr: 0 });
    rows.push([
        { content: `TOTAL (${(data || []).length})`, colSpan: 8, styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.evOuv), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.ev), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.crAvant), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.crExerc), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.cr), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(totaux.sap), styles: { halign: "right", fontStyle: "bold" } },
        "",
    ]);

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.3, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        // Marges réduites (8 mm au lieu de 14) → ~281 mm utiles en A4 paysage, plus d'espace colonnes.
        margin: { top: 32, bottom: 20, left: 8, right: 8 },
        columnStyles: {
            // 15 colonnes ≈ 276 mm (A4 paysage, 281 utiles → ~5 mm de marge de sécurité).
            // PAS d'overflow « visible » : il force la largeur d'une cellule à celle de son contenu,
            // et un N° Police long (ex. « 2-577500/20221209 ») pousse la table au-delà de la page →
            // jsPDF-autotable part en récursion infinie (« Maximum call stack size exceeded »).
            // On garde le repli (linebreak, global) : les colonnes d'ID sont assez larges pour tenir
            // sur une ligne en pratique, et à défaut le texte se replie au lieu de faire planter le PDF.
            0: { cellWidth: 26 }, 1: { cellWidth: 14 }, 2: { cellWidth: 28 }, 3: { cellWidth: 18 },
            4: { cellWidth: 16 }, 5: { cellWidth: 16 }, 6: { cellWidth: 16 }, 7: { cellWidth: 20 },
            8: { cellWidth: 18, halign: "right" }, 9: { cellWidth: 18, halign: "right" },
            10: { cellWidth: 18, halign: "right" }, 11: { cellWidth: 18, halign: "right" }, 12: { cellWidth: 18, halign: "right" },
            13: { cellWidth: 18, halign: "right" }, 14: { cellWidth: 14 },
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `${title} ${periodeTitre(date)}`,
            // Catégorie sélectionnée dans l'entête (compagnie/organisme ajoutée par drawEtatHeaderFooter).
            subtitle: `Catégorie : ${categorieCode && categorieCode !== "TOUT" ? categorieCode : "Toutes"}`,
            enteteData, logos, formattedNow,
        }),
    });

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

export const generateSinistresSurvenusPDF = (data, categorieCode, date) =>
    buildSinistresPdf({ data, date, categorieCode, title: "ÉTAT DES SINISTRES SURVENUS" });

// Export Excel des états sinistres (déclarés/survenus) avec le CUMUL DE RÈGLEMENT décomposé :
// Cumul avant exercice / Cumul exercice / Cumul total, PLUS une colonne PAR ANNÉE de règlement
// (« Payé 2011 », « Payé 2012 », …). Les années sont l'union triée présente dans le jeu de données.
const buildSinistresExcel = ({ data, date, sheetName, filePrefix }) => {
    const list = data || [];
    const anneesSet = new Set();
    list.forEach((it) => Object.keys(it?.reglementsParAnnee || {}).forEach((y) => anneesSet.add(String(y))));
    const annees = Array.from(anneesSet).sort();

    // Éval. Ouverture (statique, réintroduite) + Évaluation courante. Cumul règlement décomposé,
    // libellés alignés sur le PDF ; SAP placée APRÈS le cumul total (reste à payer final).
    const headers = [
        "N° Sinistre", "Nature", "N° Police", "Immatriculation", "Date Survenance", "Date Déclaration",
        "Lieu", "Assuré", "Tiers", "Éval. Ouverture", "Éval. courante",
        "Paiements exercices antérieurs", "Paiement de l'exercice", "Cumul des paiements", "SAP",
        ...annees.map((y) => `Payé ${y}`),
        "Statut",
    ];

    const rows = list.map((item) => ([
        item?.numeroSinistrem || item?.numeroSinistreP || item?.numSinistre || "",
        item?.nature || "",
        item?.numeroPoliceM || item?.numeroPoliceP || "",
        item?.immatriculation || "",
        item?.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
        item?.dateDeclaration ? frenchDateOnly(item.dateDeclaration) : "",
        item?.lieu || "",
        item?.prenom ? `${item.nom} ${item.prenom}` : item.nom || "",
        item?.tiers || "",
        item?.evaluationOuverture || 0,
        item?.evaluation || 0,
        item?.cumulReglementsAvant || 0,
        item?.cumulReglementsExercice || 0,
        item?.cumulReglements || 0,
        item?.sap || 0,
        ...annees.map((y) => Number(item?.reglementsParAnnee?.[y]) || 0),
        etatLabel(item?.etat),
    ]));

    // Ligne TOTAL : sommes des colonnes chiffrées (éval ouv., éval, les 3 cumuls, SAP, chaque année).
    const sum = (fn) => list.reduce((s, it) => s + (Number(fn(it)) || 0), 0);
    const totalRow = new Array(headers.length).fill("");
    totalRow[0] = `TOTAL (${list.length})`;
    totalRow[9] = sum((it) => it?.evaluationOuverture);
    totalRow[10] = sum((it) => it?.evaluation);
    totalRow[11] = sum((it) => it?.cumulReglementsAvant);
    totalRow[12] = sum((it) => it?.cumulReglementsExercice);
    totalRow[13] = sum((it) => it?.cumulReglements);
    totalRow[14] = sum((it) => it?.sap);
    annees.forEach((y, i) => { totalRow[15 + i] = sum((it) => it?.reglementsParAnnee?.[y]); });

    const ws = XLSX.utils.aoa_to_sheet([...etatCompagnieRows(), ...etatSinistreRows(), headers, ...rows, totalRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }),
        `${filePrefix}${periodeFichier(date)}_${formattedNow}.xlsx`);
};

export const generateSinistresSurvenusExcel = (data, date) =>
    buildSinistresExcel({ data, date, sheetName: "Sinistres Survenus", filePrefix: "SinistresSurvenus" });

export const generateSinistresDeclaresPDF = (data, categorieCode, date) =>
    buildSinistresPdf({ data, date, categorieCode, title: "ÉTAT DES SINISTRES DÉCLARÉS" });

export const generateSinistresDeclaresExcel = (data, date) =>
    buildSinistresExcel({ data, date, sheetName: "Sinistres Déclarés", filePrefix: "SinistresDeclares" });

// Sinistres TARDIFS = déclarés une année civile après (ou plus) l'année de survenance.
// Réutilise le rendu « déclarés » (mêmes colonnes Surv./Décl. + cumul) ; la liste est déjà
// filtrée en amont (année déclaration > année survenance).
export const generateSinistresTardifsPDF = (data, categorieCode, date) =>
    buildSinistresPdf({ data, date, categorieCode, title: "ÉTAT DES SINISTRES TARDIFS" });

export const generateSinistresTardifsExcel = (data, date) =>
    buildSinistresExcel({ data, date, sheetName: "Sinistres Tardifs", filePrefix: "SinistresTardifs" });

export const generateDecaissementsPDF = async (data, categorieCode, date) => {
    data = triExportParDateCroissante(data);
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    // Préparer les lignes
    let total = 0;
    const rows = data.map((item, index) => {
        const montant = item.montantPaye || item.montant_global || 0;
        total += montant;
        // « Date de règlement » = DATE DU CHÈQUE (dateEmissionCheque, posée à l'assignation),
        // et non la date de l'ordre de dépense (dateReglement). Repli sur dateReglement si absente.
        const dateReglementAffichee = item.dateEmissionCheque || item.dateReglement;
        return [
            dateReglementAffichee
                ? new Date(dateReglementAffichee).toLocaleDateString("fr-FR")
                : "",
            item.numeroSinistreM || item.numeroSinistreP || item.numSinistre,
            item.dateSinistre
                ? new Date(item.dateSinistre).toLocaleDateString("fr-FR")
                : "",
            `Chèque ${item.banque || ""} N° ${item.numeroCheque || ""} - ${item?.beneficiaire}`.trim(),
            displayFloorNum(montant),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
            displayFloorNum(montant / 5),
        ];
    });
    // Ajouter la ligne de total
    rows.push([
        { content: `TOTAL`, colSpan: 4, styles: { halign: "right" } },
        displayNum(total),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
        displayFloorNum(total / 5),
    ]);

    // Générer le tableau
    autoTable(doc, {
        head: [
            [
                {
                    content: "Date de règlement", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "N° Sinistre", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Date de survenance", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Justificatif de règlement", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Montant réglé", rowSpan: 2, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                },
                {
                    content: "Répartition sinistre", colSpan: 5, styles: {
                        halign: 'center', valign: 'middle',
                        fillColor: [204, 255, 255], // CCFFFF
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        textColor: 20
                    }
                }
            ],
            [
                "FIDELIA",
                "NSIA",
                "SANLAM",
                "GTA",
                "SUNU"
            ]
        ],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255], // CCFFFF
            textColor: 20,
        },
        margin: { top: 32, bottom: 22 },
        columnStyles: {
            0: { halign: 'center', cellWidth: 18 }, // Date de réglement
            1: { halign: 'center' }, // N° Sinistre
            2: { halign: 'center', cellWidth: 18 }, // Date de survenance
            3: { halign: 'left' }, // Justificatif de réglement
            4: { halign: 'right' }, // Montant réglé
            5: { halign: 'right' }, // FIDELIA
            6: { halign: 'right' }, // NSIA
            7: { halign: 'right' }, // SANLAM
            8: { halign: 'right' }, // GTA
            9: { halign: 'right' }  // SUNU
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `ÉTAT DES SINISTRES PAYÉS ${periodeTitre(date)}`,
            subtitle: categorieCode ? `Catégorie : ${categorieCode}` : "",
            enteteData, logos, formattedNow,
        }),
    });


    // doc.save(`Etats des Sinistres du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])} _ ${formattedNow}.pdf`);
    finalizeEtatPdf(doc);
    return doc.output('datauristring');
};

export const generateDecaissementsExcel = async (data, codeCategorie, date) => {
    data = triExportParDateCroissante(data);

    const wsData = [];
    let total = 0;

    // En-tête du tableau
    wsData.push(
        ["POOL TPVM-VT GIE"],
        ...etatCompagnieRows(), ...etatSinistreRows(),
        [
            `ÉTAT DES SINISTRES PAYÉS ${periodeTitre(date)}`
        ],
        [`Catégorie : ${codeCategorie}`],
        [""], // Ligne vide
        [
            "Date de règlement",
            "N° Sinistre",
            "Bénéficiaire",
            "Date de survenance",
            "Justificatif de règlement",
            "Montant réglé",
            "FIDELIA",
            "NSIA",
            "SANLAM",
            "GTA",
            "SUNU"
        ]);

    // Contenu
    data.forEach((item) => {
        const montant = item.montantPaye || item.montant_global || 0;
        total += montant;

        const repartition = Array(5).fill(montant / 5); // réparti également

        // « Date de règlement » = DATE DU CHÈQUE (dateEmissionCheque), repli sur dateReglement.
        const dateReglementAffichee = item.dateEmissionCheque || item.dateReglement;
        wsData.push([
            dateReglementAffichee
                ? new Date(dateReglementAffichee).toLocaleDateString("fr-FR")
                : "",
            item.numeroSinistreM || item.numeroSinistreP || item.numSinistre || "",
            item.beneficiaire || "",
            item.dateSinistre
                ? new Date(item.dateSinistre).toLocaleDateString("fr-FR")
                : "",
            `Chèque ${item.banque || ""} N° ${item.numeroCheque || ""}`.trim(),
            montant,
            ...repartition
        ]);
    });

    // Ligne TOTAL (une colonne « Bénéficiaire » insérée → « TOTAL » sous « Justificatif »)
    const totalRow = [
        "", "", "", "", "TOTAL",
        total,
        total / 5,
        total / 5,
        total / 5,
        total / 5,
        total / 5
    ];
    wsData.push(totalRow);

    // Créer la feuille Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Mise en forme : alignement à droite pour montants (montant réglé + 5 compagnies = index 5..10)
    const columnStyles = [
        {}, {}, {}, {}, {},
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } },
        { alignment: { horizontal: "right" } }
    ];

    Object.keys(ws).forEach((cell) => {
        if (!cell.startsWith("!")) {
            const colIdx = XLSX.utils.decode_cell(cell).c;
            if (columnStyles[colIdx]) {
                ws[cell].s = columnStyles[colIdx];
            }
        }
    });

    XLSX.utils.book_append_sheet(wb, ws, "Décaissements");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const blob = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true
    });

    saveAs(
        new Blob([blob], { type: "application/octet-stream" }),
        `Etat_Sinistres_${periodeFichier(date)}_${formattedNow}.xlsx`
    );

};

export const generateDecaissementsHonorairesPDFOld = async (data, categorieCode, date) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    // Grouper par bénéficiaire (toUpperCase)
    const grouped = data.reduce((acc, curr) => {
        const key = (curr.beneficiaire || 'INCONNU').toUpperCase();
        if (!acc[key]) acc[key] = [];
        acc[key].push(curr);
        return acc;
    }, {});

    console.log("grouped", grouped);

    const frenchDateOnly = (date) =>
        new Date(date).toLocaleDateString("fr-FR");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight()

    // 🔵 EN-TÊTE UNIQUE
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 255);
    doc.text("POOL TPVM-VT GIE", 14, 15);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(
        `ÉTAT DES RETENUES SUR HONORAIRES PAYÉS ${periodeTitre(date)}`,
        pageWidth / 2,
        25,
        { align: "center" }
    );

    doc.setLineWidth(0.5);
    doc.line(14, 30, pageWidth - 14, 30);

    let currentY = 35; // Position après entête

    // 🔁 POUR CHAQUE BÉNÉFICIAIRE
    for (const [beneficiaire, decaissements] of Object.entries(grouped)) {
        // Saut de page si nécessaire
        if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = 20;
        }
        // Nom du bénéficiaire
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(beneficiaire, 14, currentY);

        console.log("decaissements", decaissements);
        console.log("beneficiaire", beneficiaire);

        let total = 0;
        const rows = decaissements.map((item) => {
            const montant = item.retenue || 0;
            total += montant;
            return [
                item.dateReglement
                    ? new Date(item.dateReglement).toLocaleDateString("fr-FR")
                    : "",
                item.numeroSinistreM || item.numeroSinistreP || item.numSinistre,
                item.motifReglement || "",
                displayNum(montant)
            ];
        });

        // Ligne de total par groupe
        rows.push([
            { content: `TOTAL`, colSpan: 3, styles: { halign: "right", fontStyle: 'bold' } },
            displayNum(total)
        ]);

        const result = autoTable(doc, {
            head: [["Date règlement", "N° sinistre", "Motif règlement", "Montant"]],
            body: rows,
            // startY: currentY + 15 || 50,
            theme: "grid",
            styles: {
                fontSize: 8,
                halign: 'left'
            },
            columnStyles: {
                0: { halign: 'center', cellWidth: 35 },
                1: { halign: 'center', cellWidth: 35 },
                2: { halign: 'left', cellWidth: 100 },
                3: { halign: 'right', cellWidth: 25 }
            },
            margin: { left: 14, right: 14 },
            pageBreak: 'auto',
        });
        console.log("result", result)
        // currentY = result.finalY + 10;

        if (result && result.finalY) {
            currentY = result.finalY + 10;
        } else {
            currentY += 40; // valeur par défaut si le tableau n'est pas rendu
        }

    }

    // 🔚 Pied de page (ajouté sur la dernière page uniquement)
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Généré le : ${formattedNow} par PoolIns`, 14, pageHeight - 10);
    doc.setFontSize(9);
    doc.text(`Page ${pageCount} / ${pageCount}`, pageWidth - 20, pageHeight - 10);

    finalizeEtatPdf(doc);
    return doc.output('datauristring');
};

const drawRHPHeader = (doc, pageWidth, formattedDateTime, date, enteteData) => {
    if (enteteData?.url) {
        const h = 10;
        const w = Math.min(72, h * (enteteData.ratio || 4));
        try { doc.addImage(enteteData.url, "PNG", 14, 6, w, h); } catch (e) { /* noop */ }
    } else {
        doc.setFontSize(14).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text("POOL TPVM-VT GIE", 14, 12);
    }
    doc.setFontSize(11).setFont("helvetica", "bold").setTextColor(NAVY[0], NAVY[1], NAVY[2]);
    doc.text(`ÉTAT DES RETENUES SUR HONORAIRES PAYÉS ${periodeTitre(date)}`,
        pageWidth / 2, 22, { align: "center" });
    doc.setDrawColor(NAVY[0], NAVY[1], NAVY[2]).setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
};

const drawRHPFooter = (doc, formattedDateTime, logos) => {
    const pageCount = doc.getNumberOfPages();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.height;
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        if (logos?.length) {
            const lh = 5, gap = 4;
            const widths = logos.map((l) => lh * (l.ratio || 2));
            const total = widths.reduce((a, b) => a + b, 0) + gap * (logos.length - 1);
            const scale = total > (pw - 28) ? (pw - 28) / total : 1;
            let x = (pw - total * scale) / 2;
            const y = ph - 15;
            logos.forEach((l, idx) => {
                const w = widths[idx] * scale;
                try { doc.addImage(l.url, "PNG", x, y, w, lh * scale); } catch (e) { /* noop */ }
                x += w + gap * scale;
            });
        }
        doc.setDrawColor(210, 210, 210).setLineWidth(0.3);
        doc.line(14, ph - 8.5, pw - 14, ph - 8.5);
        doc.setFontSize(8).setFont("helvetica", "normal").setTextColor(120, 120, 120);
        doc.text(`Page ${i} sur ${pageCount} | Généré le : ${formattedDateTime}`, pw / 2, ph - 5, { align: "center" });
    }
};

export const generateDecaissementsHonorairesPDF = async (data, categorieCode, date) => {
    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedDateTime = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    const pageWidth = doc.internal.pageSize.getWidth();
    drawRHPHeader(doc, pageWidth, formattedDateTime, date, enteteData);

    let initialY = 28;

    // 1. Groupement des données par nom_beneficiaire
    const groupedDecaissements = {};
    data.forEach((item) => {
        const beneficiaireNom = item.beneficiaire || 'INCONNU'; // Gérer les noms nuls
        if (!groupedDecaissements[beneficiaireNom]) {
            groupedDecaissements[beneficiaireNom] = [];
        }
        groupedDecaissements[beneficiaireNom].push(item);
    });

    // Trier les bénéficiaires par nom
    const sortedBeneficiaires = Object.keys(groupedDecaissements).sort();

    // En-têtes du tableau
    const tableHeaders = [
        "Date Reglement",
        "N° Sinistre",
        "Motif Reglement",
        "Montant",
    ];

    // Styles généraux pour le tableau
    const defaultTableStyles = {
        headStyles: {
            fillColor: [204, 255, 255], // CCFFFF
            fontSize: 8,
            halign: 'center',
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            textColor: 20
        },
        styles: {
            fontSize: 7.5,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'middle',
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        columnStyles: {
            // Aligner les montants à droite
            3: { halign: 'right' }, // Montant
            4: { halign: 'right' }  // Retenue
        },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        margin: { top: 10, bottom: 20 }
    };

    // Parcourir chaque groupe de bénéficiaires
    sortedBeneficiaires.forEach((beneficiaireNom) => {
        const decaissementsForBeneficiaire = groupedDecaissements[beneficiaireNom];
        console.log(decaissementsForBeneficiaire);
        // Vérifier si la page est pleine avant d'ajouter le nouveau groupe
        let yPos = doc.lastAutoTable?.finalY || initialY;
        if (yPos + 20 > doc.internal.pageSize.height - 30) { // Espace suffisant pour titre + début tableau
            doc.addPage();
            drawRHPHeader(doc, pageWidth, formattedDateTime, date, enteteData);
            yPos = 28; // Réinitialiser la position Y pour la nouvelle page
        }

        // Afficher le nom du bénéficiaire en majuscules
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold'); // Mettre en gras
        doc.text(beneficiaireNom.toUpperCase(), 14, yPos + 10);
        doc.setFont(undefined, 'normal'); // Revenir à la normale
        yPos += 15; // Espace après le nom du bénéficiaire

        // Préparer les lignes de données pour autoTable
        let totalMontant = 0;
        let totalRetenue = 0;

        const dataRows = decaissementsForBeneficiaire.map((d, id) => {
            const montant = Math.trunc(d.montant || 0);
            const retenue = Math.trunc(d.retenue || 0); // Utiliser 'retenue' de la fonction SQL

            totalMontant += montant;
            totalRetenue += retenue;

            return [
                d.dateReglement ? new Date(d.dateReglement).toLocaleDateString("fr-FR") : "",
                d.numeroSinistreM || d.numeroSinistreP, // Utiliser l'un ou l'autre
                d.motifReglement,
                new Intl.NumberFormat("fr-FR").format(retenue).replace(/\s/g, ' ')
            ];
        });

        // Ajouter la ligne de total pour ce bénéficiaire
        const totalRow = [
            { content: 'TOTAL', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 255, 230] } },
            { content: new Intl.NumberFormat("fr-FR").format(totalRetenue).replace(/\s/g, ' '), styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 255, 230] } }
        ];
        dataRows.push(totalRow);
        console.log(totalMontant);


        // Générer le tableau pour le groupe actuel
        autoTable(doc, {
            startY: yPos,
            head: [tableHeaders],
            body: dataRows,
            ...defaultTableStyles, // Appliquer les styles par défaut
            didParseCell: (data) => {
                // Si c'est la ligne de total, appliquer un style spécifique aux cellules
                if (data.row.index === dataRows.length - 1) { // Dernière ligne (le total)
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [230, 255, 230]; // Vert clair pour les totaux
                    if (data.column.index >= 3) { // Montant et Retenue
                        data.cell.styles.halign = 'right';
                    }
                }
            }
        });
    });

    drawRHPFooter(doc, formattedDateTime, logos);
    finalizeEtatPdf(doc);
    return doc.output('datauristring');
};

export const generateRatioSP = async (data, date) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
    });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    // Préparer les lignes
    let nombreContrats = 0;
    let primesTotales = 0;
    let nombreSinistres = 0;
    let montantSinistres = 0;
    const rows = data.map((item, index) => {
        nombreContrats += item.nombreContrats || 0;
        primesTotales += item.primesTotales || 0;
        nombreSinistres += item.nombreSinistres || 0;
        montantSinistres += item.montantSinistres || 0;
        return [
            item.branche || item.categorie,
            displayNum(item.nombreContrats),
            displayNum(item.primesTotales),
            displayNum(item.nombreSinistres),
            displayNum(item.montantSinistres),
            item.ratioSP,
        ];
    });
    // Ajouter la ligne de total
    rows.push([
        { content: `TOTAL`, styles: { halign: "right" } },
        displayNum(nombreContrats),
        displayNum(Math.floor(primesTotales)),
        displayNum(nombreSinistres),
        displayNum(montantSinistres),
        displayNum(Math.floor((montantSinistres / primesTotales) * 100)),
    ]);

    // Générer le tableau
    autoTable(doc, {
        head: [
            ["Branche",
                "Nombre de contrats",
                "Primes encaissées (P)",
                "Nombre de sinistres",
                "Montant des sinistres (S)",
                "Ratio SP"],
        ],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
        },
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255], // CCFFFF
            textColor: 20,
        },
        margin: { top: 32, bottom: 22 },
        columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'center' },
            2: { halign: 'right' },
            3: { halign: 'center' },
            4: { halign: 'right' },
            5: { halign: 'right' },
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `RATIO S/P PÉRIODE ${periodeTitre(date)}`,
            subtitle: "",
            enteteData, logos, formattedNow,
        }),
    });


    // doc.save(`Etats des Sinistres du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])} _ ${formattedNow}.pdf`);
    finalizeEtatPdf(doc);
    return doc.output('datauristring');
};

export const generateBonAPayerPDF = async (data, categorieCode, date) => {
    data = triExportParDateCroissante(data);
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    const headers = [
        "Bénéficiaire", "Montant à Payer", "N° Sinistre", "Date Sinistre", "Police",
        "Motif Règlement", "Date Ordre de dépense"
    ];

    let total = 0;
    const rows = data.map(item => {
        const montant = item.montantPaye || 0;
        total += montant;
        return [
            item.beneficiaire || "",
            displayNum(montant),
            item.numeroSinistreM || item.numeroSinistreP,
            item.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
            item.numeroPoliceM || item.numeroPoliceP || "",
            item.motifReglement || "",
            item.dateReglement ? frenchDateOnly(item.dateReglement) : "",
        ];
    });

    // Ligne de total (le total s'aligne SOUS la colonne « Montant à Payer »)
    rows.push([
        { content: "TOTAL", styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(total), styles: { halign: "right", fontStyle: "bold" } },
        "", "", "", "", ""
    ]);

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "grid",
        styles: {
            fontSize: 7.5, cellPadding: 1.3, lineWidth: 0.1, lineColor: [215, 215, 225],
            textColor: [45, 45, 45], overflow: "linebreak", valign: "middle",
        },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 22, left: 14, right: 14 },
        columnStyles: {
            0: { cellWidth: 48, halign: "left" },   // Bénéficiaire
            1: { cellWidth: 28, halign: "right" },  // Montant à Payer
            2: { cellWidth: 30, halign: "center" }, // N° Sinistre
            3: { cellWidth: 26, halign: "center" }, // Date Sinistre
            4: { cellWidth: 36, halign: "center" }, // Police
            5: { cellWidth: 70, halign: "left" },   // Motif Règlement
            6: { cellWidth: 26, halign: "center" }, // Date Ordre de dépense
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `ÉTAT DES SINISTRES BON À PAYER ${periodeTitre(date)}`,
            subtitle: categorieCode ? `Catégorie : ${categorieCode}` : "",
            enteteData, logos, formattedNow,
        }),
    });

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

export const generateBonAPayerExcel = (data, codeCategorie, date) => {
    data = triExportParDateCroissante(data);
    const wsData = [];
    let total = 0;
    console.log("date:", date);
    wsData.push(
        ["POOL TPVM-VT GIE"],
        ...etatCompagnieRows(), ...etatSinistreRows(),
        [`ÉTAT DES SINISTRES BON À PAYER ${periodeTitre(date)}`],
        [`Catégorie : ${codeCategorie}`],
        [""],
        [
            "Bénéficiaire", "Montant à Payer", "N° Sinistre", "Date Sinistre", "Police",
            "Motif Règlement", "Date Ordre de dépense",
        ]
    );

    data.forEach(item => {
        const montant = item.montantPaye || 0;
        total += montant;
        wsData.push([
            item.beneficiaire || "",
            displayNum(montant),
            item.numeroSinistreM || item.numeroSinistreP,
            item.dateSinistre ? frenchDateOnly(item.dateSinistre) : "",
            item.numeroPoliceM || item.numeroPoliceP || "",
            item.motifReglement || "",
            item.dateReglement ? frenchDateOnly(item.dateReglement) : "",
        ]);
    });

    wsData.push(["TOTAL", total, "", "", "", "", ""]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bon à Payer");

    const now = new Date();
    const formattedNow = now.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
        new Blob([blob], { type: "application/octet-stream" }),
        `Etat_Sinistres_Bon_A_Payer_${periodeFichier(date)}_${formattedNow}.xlsx`
    );
};

// ============================================================================
// État de synthèse des paiements d'un conseil juridique (1 ligne/dossier, postes
// pivotés). Données = /api/decaissement/conseils-juridiques/synthese.
// ============================================================================
const SYNTHESE_HEADERS = [
    "N° Sinistre", "Juridiction", "Date Sin.", "Année", "N° Police", "Assuré",
    "Gestion", "PV", "Vignette", "Déplac.", "Honoraires", "TVA", "Retenue", "Net à payer",
];

const syntheseTotaux = (data) => (data || []).reduce((a, it) => ({
    g: a.g + (Number(it?.fraisGestion) || 0),
    pv: a.pv + (Number(it?.fraisPvConstat) || 0),
    vg: a.vg + (Number(it?.fraisVignette) || 0),
    dp: a.dp + (Number(it?.fraisDeplacement) || 0),
    ho: a.ho + (Number(it?.honoraires) || 0),
    tva: a.tva + (Number(it?.tva) || 0),
    ret: a.ret + (Number(it?.retenue) || 0),
    net: a.net + (Number(it?.montantNet) || 0),
}), { g: 0, pv: 0, vg: 0, dp: 0, ho: 0, tva: 0, ret: 0, net: 0 });

const synthesePeriode = (date) => (date && date[0] && date[1])
    ? `${periodeTitre(date)}` : "";

export const generateConseilSynthesePDF = async (data, conseilNom, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    const rows = (data || []).map((it) => ([
        it?.numeroSinistre || "",
        it?.juridiction || "",
        it?.dateSinistre ? frenchDateOnly(it.dateSinistre) : "",
        it?.anneeSurvenance || "",
        it?.numeroPolice || "",
        it?.assure || "",
        displayNum(it?.fraisGestion || 0),
        displayNum(it?.fraisPvConstat || 0),
        displayNum(it?.fraisVignette || 0),
        displayNum(it?.fraisDeplacement || 0),
        displayNum(it?.honoraires || 0),
        displayNum(it?.tva || 0),
        displayNum(it?.retenue || 0),
        displayNum(it?.montantNet || 0),
    ]));

    const t = syntheseTotaux(data);
    rows.push([
        { content: `TOTAL (${(data || []).length})`, colSpan: 6, styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.g), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.pv), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.vg), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.dp), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.ho), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.tva), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.ret), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(t.net), styles: { halign: "right", fontStyle: "bold" } },
    ]);

    autoTable(doc, {
        head: [SYNTHESE_HEADERS],
        body: rows,
        theme: "grid",
        styles: { fontSize: 6.5, cellPadding: 1.2, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 10, right: 10 },
        columnStyles: {
            6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" },
            10: { halign: "right" }, 11: { halign: "right" }, 12: { halign: "right" }, 13: { halign: "right" },
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `ÉTAT SYNTHÈSE DE PAIEMENT DES FRAIS ET HONORAIRES${conseilNom ? ` DUS À ${conseilNom}` : ""} ${synthesePeriode(date)}`,
            subtitle: "",
            enteteData, logos, formattedNow,
        }),
    });

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

export const generateConseilSyntheseExcel = (data, conseilNom, date) => {
    const t = syntheseTotaux(data);
    const rows = (data || []).map((it) => ([
        it?.numeroSinistre || "",
        it?.juridiction || "",
        it?.dateSinistre ? frenchDateOnly(it.dateSinistre) : "",
        it?.anneeSurvenance || "",
        it?.numeroPolice || "",
        it?.assure || "",
        Number(it?.fraisGestion) || 0,
        Number(it?.fraisPvConstat) || 0,
        Number(it?.fraisVignette) || 0,
        Number(it?.fraisDeplacement) || 0,
        Number(it?.honoraires) || 0,
        Number(it?.tva) || 0,
        Number(it?.retenue) || 0,
        Number(it?.montantNet) || 0,
    ]));
    const totalRow = ["TOTAL", "", "", "", "", "", t.g, t.pv, t.vg, t.dp, t.ho, t.tva, t.ret, t.net];

    const wsData = [
        ["POOL TPVM-VT GIE"],
        [`ÉTAT SYNTHÈSE DE PAIEMENT DES FRAIS ET HONORAIRES${conseilNom ? ` DUS À ${conseilNom}` : ""}`],
        [synthesePeriode(date)],
        [""],
        SYNTHESE_HEADERS,
        ...rows,
        totalRow,
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Synthèse conseil");
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }),
        `Synthese_Conseil_Juridique_${formattedNow}.xlsx`);
};

// ============================================================================
// État « Sinistres en contentieux » (situation des dossiers affectés à un cabinet).
// Données = /api/sinistre/contentieux/etat.
// ============================================================================
const CONTENTIEUX_HEADERS = [
    "N° Sinistre", "Juridiction", "Année", "Date Sinistre", "Assuré",
    "Évaluation", "Montant total payé", "SAP", "Résumé du dossier",
];

export const generateContentieuxPDF = async (data, cabinetNom, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    let tEval = 0, tPaye = 0, tSap = 0;
    const rows = (data || []).map((it) => {
        tEval += Number(it?.evaluation) || 0;
        tPaye += Number(it?.montantTotalPaye) || 0;
        tSap += Number(it?.sap) || 0;
        return [
            it?.numeroSinistre || "",
            it?.juridiction || "",
            it?.anneeSurvenance || "",
            it?.dateSinistre ? frenchDateOnly(it.dateSinistre) : "",
            it?.assure || "",
            displayNum(it?.evaluation || 0),
            displayNum(it?.montantTotalPaye || 0),
            displayNum(it?.sap || 0),
            it?.resumeDossier || "",
        ];
    });
    rows.push([
        { content: `TOTAL (${(data || []).length})`, colSpan: 5, styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(tEval), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(tPaye), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(tSap), styles: { halign: "right", fontStyle: "bold" } },
        "",
    ]);

    autoTable(doc, {
        head: [CONTENTIEUX_HEADERS],
        body: rows,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.3, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 12, right: 12 },
        columnStyles: {
            5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { cellWidth: 70 },
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `SITUATION DES DOSSIERS EN CONTENTIEUX${cabinetNom ? ` AFFECTÉS AU CABINET ${cabinetNom}` : ""} ${synthesePeriode(date)}`,
            subtitle: "",
            enteteData, logos, formattedNow,
        }),
    });
    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

export const generateContentieuxExcel = (data, cabinetNom, date) => {
    let tEval = 0, tPaye = 0, tSap = 0;
    const rows = (data || []).map((it) => {
        tEval += Number(it?.evaluation) || 0;
        tPaye += Number(it?.montantTotalPaye) || 0;
        tSap += Number(it?.sap) || 0;
        return [
            it?.numeroSinistre || "",
            it?.juridiction || "",
            it?.anneeSurvenance || "",
            it?.dateSinistre ? frenchDateOnly(it.dateSinistre) : "",
            it?.assure || "",
            Number(it?.evaluation) || 0,
            Number(it?.montantTotalPaye) || 0,
            Number(it?.sap) || 0,
            it?.resumeDossier || "",
        ];
    });
    const totalRow = ["TOTAL", "", "", "", "", tEval, tPaye, tSap, ""];
    const wsData = [
        ["POOL TPVM-VT GIE"],
        [`SITUATION DES DOSSIERS EN CONTENTIEUX${cabinetNom ? ` AFFECTÉS AU CABINET ${cabinetNom}` : ""}`],
        [synthesePeriode(date)],
        [""],
        CONTENTIEUX_HEADERS,
        ...rows,
        totalRow,
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contentieux");
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `Sinistres_En_Contentieux_${formattedNow}.xlsx`);
};

// ============================================================================
// CADENCE DE RÈGLEMENT (triangle de liquidation) : lignes = année de survenance,
// colonnes = année de règlement. Chaque cellule = nb règlements + Σ montant.
// L'API renvoie une liste « longue » de cellules (CadenceCelluleDTO), années déjà collapsées en seaux :
//   { typeLigne: "PAIEMENT",   survenanceAnnee, paiementAnnee, nbSinistres (distincts), nbReglements, montant }
//   { typeLigne: "TOTAL_SURV", survenanceAnnee, paiementAnnee: null, nbSinistres dédupliqués sur l'année, nbReglements, montant }
//   { typeLigne: "DECLARE",    survenanceAnnee, paiementAnnee: null, nbSinistres = nb déclarés, nbReglements: 0, montant: 0 }
// buildCadenceModel() pivote ces cellules en triangle prêt à afficher/exporter.
// ============================================================================

// 4 années explicites (la plus récente en tête) + 1 seau « et antérieur ».
export const CADENCE_NB_ANNEES = 5;

// Couleurs de développement (âge = année règlement − année survenance).
// Barème vert « même année → +4 ans+ » ; seaux historiques (« et ant. ») en jaune « reprise ».
export const CADENCE_COLORS = {
    age0: [232, 245, 233], age1: [200, 230, 201], age2: [165, 214, 167],
    age3: [129, 199, 132], age4: [102, 187, 106], reprise: [255, 249, 196],
};

// Renvoie la couleur [r,g,b] d'une cellule (rb/cb = seaux ligne/colonne) ou null (région vide).
export const cadenceCellRGB = (rb, cb) => {
    if (!rb || !cb) return null;
    if (rb.isTail || cb.isTail) return CADENCE_COLORS.reprise; // reprise historique
    const age = cb.year - rb.year;                 // règlement APRÈS survenance = développement normal
    if (age < 0) return null;                       // règlement avant survenance = impossible → vide
    if (age === 0) return CADENCE_COLORS.age0;
    if (age === 1) return CADENCE_COLORS.age1;
    if (age === 2) return CADENCE_COLORS.age2;
    if (age === 3) return CADENCE_COLORS.age3;
    return CADENCE_COLORS.age4;
};

export const buildCadenceModel = (cells) => {
    const list = Array.isArray(cells) ? cells : [];
    const paiements = list.filter((c) => c?.typeLigne === "PAIEMENT" && c?.survenanceAnnee != null && c?.paiementAnnee != null);
    const totauxSurv = list.filter((c) => c?.typeLigne === "TOTAL_SURV" && c?.survenanceAnnee != null);
    const declares = list.filter((c) => c?.typeLigne === "DECLARE" && c?.survenanceAnnee != null);

    // Les années sont déjà « collapsées » par le SQL (seaux ref..ref-4). refYear = la plus récente.
    let refYear = 0;
    [...paiements, ...totauxSurv, ...declares].forEach((c) => {
        if (c.survenanceAnnee) refYear = Math.max(refYear, Number(c.survenanceAnnee));
        if (c.paiementAnnee) refYear = Math.max(refYear, Number(c.paiementAnnee));
    });
    if (!refYear) refYear = new Date().getFullYear();

    const NB = CADENCE_NB_ANNEES;
    const tailYear = refYear - (NB - 1);               // ex. 2022 → seau « 2022 et ant. »
    const buckets = [];
    for (let i = 0; i < NB - 1; i++) {
        const y = refYear - i;
        buckets.push({ key: String(y), year: y, isTail: false, label: String(y) });
    }
    buckets.push({ key: "tail", year: tailYear, isTail: true, label: `${tailYear} et ant.` });

    const bucketOf = (year) => {
        const y = Number(year);
        if (!y) return null;
        if (y > refYear) return buckets[0];
        if (y <= tailYear) return buckets[NB - 1];
        return buckets[refYear - y];
    };

    // Cellule : nbSinistres = sinistres distincts, nbReglements = nb de décaissements, montant.
    const emptyCell = () => ({ nbSinistres: 0, nbReglements: 0, montant: 0 });
    const rows = buckets.map((rb) => ({
        bucket: rb,
        cells: Object.fromEntries(buckets.map((cb) => [cb.key, emptyCell()])),
        // Totaux ligne DÉDUPLIQUÉS (issus des lignes TOTAL_SURV, pas de la somme des cellules).
        totalNbSinistres: 0, totalNbReglements: 0, totalMontant: 0, declares: 0, taux: 0,
    }));
    const rowByKey = Object.fromEntries(rows.map((r) => [r.bucket.key, r]));

    paiements.forEach((c) => {
        const rb = bucketOf(c.survenanceAnnee);
        const cb = bucketOf(c.paiementAnnee);
        if (!rb || !cb) return;
        const cell = rowByKey[rb.key].cells[cb.key];
        cell.nbSinistres += Number(c.nbSinistres) || 0;
        cell.nbReglements += Number(c.nbReglements) || 0;
        cell.montant += Number(c.montant) || 0;
    });
    totauxSurv.forEach((c) => {
        const rb = bucketOf(c.survenanceAnnee);
        if (!rb) return;
        const r = rowByKey[rb.key];
        r.totalNbSinistres += Number(c.nbSinistres) || 0;
        r.totalNbReglements += Number(c.nbReglements) || 0;
        r.totalMontant += Number(c.montant) || 0;
    });
    declares.forEach((c) => {
        const rb = bucketOf(c.survenanceAnnee);
        if (!rb) return;
        rowByKey[rb.key].declares += Number(c.nbSinistres) || 0;
    });

    rows.forEach((r) => { r.taux = r.declares > 0 ? (r.totalNbSinistres / r.declares) * 100 : 0; });

    // Colonnes (pied) : somme des cellules (chaque sinistre appartient à une seule année de survenance,
    // donc à une seule ligne → sommer les cellules d'une colonne dédoublonne correctement).
    const totalCol = Object.fromEntries(buckets.map((cb) => [cb.key, emptyCell()]));
    let gSin = 0, gReg = 0, gMt = 0, gDec = 0;
    rows.forEach((r) => {
        buckets.forEach((cb) => {
            totalCol[cb.key].nbSinistres += r.cells[cb.key].nbSinistres;
            totalCol[cb.key].nbReglements += r.cells[cb.key].nbReglements;
            totalCol[cb.key].montant += r.cells[cb.key].montant;
        });
        // Grand total DÉDUPLIQUÉ = somme des totaux ligne (chaque sinistre est dans une seule ligne).
        gSin += r.totalNbSinistres; gReg += r.totalNbReglements; gMt += r.totalMontant; gDec += r.declares;
    });
    const totals = {
        cells: totalCol, totalNbSinistres: gSin, totalNbReglements: gReg, totalMontant: gMt, declares: gDec,
        taux: gDec > 0 ? (gSin / gDec) * 100 : 0,
    };

    return { refYear, tailYear, buckets, rows, totals };
};

const cadencePeriode = (date) =>
    (date && date[0]) ? `Du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])}` : "Toutes périodes";

export const generateCadencePDF = async (model, date) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
    const { buckets, rows, totals } = model || { buckets: [], rows: [], totals: null };

    // En-tête sur 2 rangées : « Payés en YYYY » (nb + mt) + TOTAL + Déc. + Taux.
    const head = [
        [
            { content: "Survenus en", rowSpan: 2, styles: { valign: "middle", halign: "left" } },
            ...buckets.map((b) => ({ content: `Payés en ${b.label}`, colSpan: 2, styles: { halign: "center" } })),
            { content: "TOTAL", colSpan: 2, styles: { halign: "center" } },
            { content: "Déc.", rowSpan: 2, styles: { valign: "middle", halign: "right" } },
            { content: "Taux", rowSpan: 2, styles: { valign: "middle", halign: "right" } },
        ],
        [
            ...buckets.flatMap(() => ["sin. (règl.)", "mt (FCFA)"]),
            "sin. (règl.)", "mt (FCFA)",
        ],
    ];

    const nbTxt = (sin, reg) => `${displayNum(sin)} (${displayNum(reg)})`;
    const numCell = (v, rgb) => ({ content: v, styles: { halign: "right", ...(rgb ? { fillColor: rgb } : {}) } });
    const body = rows.map((r) => ([
        { content: r.bucket.label, styles: { halign: "left", fontStyle: "bold" } },
        ...buckets.flatMap((cb) => {
            const c = r.cells[cb.key];
            const rgb = cadenceCellRGB(r.bucket, cb);
            const has = c.nbSinistres || c.nbReglements || c.montant;
            return [
                numCell(has ? nbTxt(c.nbSinistres, c.nbReglements) : "—", rgb),
                numCell(has ? displayNum(c.montant) : "—", rgb),
            ];
        }),
        { content: nbTxt(r.totalNbSinistres, r.totalNbReglements), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(r.totalMontant), styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(r.declares), styles: { halign: "right" } },
        { content: `${r.taux.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`, styles: { halign: "right", fontStyle: "bold" } },
    ]));

    if (totals) {
        body.push([
            { content: "TOTAL", styles: { halign: "left", fontStyle: "bold", fillColor: NAVY, textColor: 255 } },
            ...buckets.flatMap((cb) => ([
                { content: nbTxt(totals.cells[cb.key].nbSinistres, totals.cells[cb.key].nbReglements), styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
                { content: displayNum(totals.cells[cb.key].montant), styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
            ])),
            { content: nbTxt(totals.totalNbSinistres, totals.totalNbReglements), styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
            { content: displayNum(totals.totalMontant), styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
            { content: displayNum(totals.declares), styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
            { content: `${totals.taux.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`, styles: { halign: "right", fontStyle: "bold", fillColor: [238, 240, 247] } },
        ]);
    }

    autoTable(doc, {
        head, body, theme: "grid",
        styles: { fontSize: 6.5, cellPadding: 1.4, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY, halign: "center" },
        margin: { top: 32, bottom: 20, left: 14, right: 14 },
        columnStyles: { 0: { cellWidth: 22 } },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: "CADENCE DE RÈGLEMENT DES SINISTRES",
            subtitle: cadencePeriode(date),
            enteteData, logos, formattedNow,
        }),
    });

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

// ============================================================================
// RÈGLEMENTS MÉDICAUX / MÉCANIQUES : liste plate (experts + établissements).
// data = ReglementDisciplineDTO[] : source, typeLibelle, beneficiaire, numeroSinistre,
//        dateSinistre, dateReglement, montant, numeroCheque, banque, motif, numeroPolice,
//        categorieCode, organisme.
// ============================================================================
const disciplineSourceLabel = (r) => (r?.source === "EXPERT" ? "Expert" : "Établissement");

export const generateDisciplinePDF = async (data, disciplineLabel, categorieCode, date) => {
    const list = triExportParDateCroissante(data, (r) => r?.dateReglement);
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    const headers = ["N° Sinistre", "Bénéficiaire", "Source", "Type", "Date Sin.", "Date Règl.",
        "Montant", "N° Chèque", "Motif", "Police", "Cat.", "Organisme"];
    let total = 0;
    const rows = list.map((r) => {
        total += Number(r?.montant) || 0;
        return [
            r?.numeroSinistre || "",
            r?.beneficiaire || "",
            disciplineSourceLabel(r),
            r?.typeLibelle || "",
            r?.dateSinistre ? frenchDateOnly(r.dateSinistre) : "",
            r?.dateReglement ? frenchDateOnly(r.dateReglement) : "",
            displayNum(r?.montant || 0),
            r?.numeroCheque || "",
            r?.motif || "",
            r?.numeroPolice || "",
            r?.categorieCode || "",
            r?.organisme || "",
        ];
    });
    rows.push([
        { content: `TOTAL (${list.length})`, colSpan: 6, styles: { halign: "right", fontStyle: "bold" } },
        { content: displayNum(total), styles: { halign: "right", fontStyle: "bold" } },
        "", "", "", "", "",
    ]);

    autoTable(doc, {
        head: [headers], body: rows, theme: "grid",
        styles: { fontSize: 6.5, cellPadding: 1.2, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 14, right: 14 },
        columnStyles: { 6: { halign: "right" } },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `ÉTAT DES RÈGLEMENTS ${disciplineLabel}${(date && date[0]) ? ` ${periodeTitre(date)}` : ""}`,
            subtitle: categorieCode && categorieCode !== "TOUT" ? `Branche : ${categorieCode}` : "",
            enteteData, logos, formattedNow,
        }),
    });
    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

export const generateDisciplineExcel = (data, filePrefix, date) => {
    const list = triExportParDateCroissante(data, (r) => r?.dateReglement);
    const headers = ["N° Sinistre", "Bénéficiaire", "Source", "Type", "Date Sinistre", "Date Règlement",
        "Montant", "N° Chèque", "Banque", "Motif", "Police", "Catégorie", "Organisme"];
    const rows = list.map((r) => ([
        r?.numeroSinistre || "", r?.beneficiaire || "", disciplineSourceLabel(r), r?.typeLibelle || "",
        r?.dateSinistre ? frenchDateOnly(r.dateSinistre) : "",
        r?.dateReglement ? frenchDateOnly(r.dateReglement) : "",
        Number(r?.montant) || 0, r?.numeroCheque || "", r?.banque || "", r?.motif || "",
        r?.numeroPolice || "", r?.categorieCode || "", r?.organisme || "",
    ]));
    const total = list.reduce((s, r) => s + (Number(r?.montant) || 0), 0);
    const totalRow = new Array(headers.length).fill("");
    totalRow[0] = `TOTAL (${list.length})`;
    totalRow[6] = total;

    const ws = XLSX.utils.aoa_to_sheet([...etatCompagnieRows(), ...etatSinistreRows(), headers, ...rows, totalRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, filePrefix);
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `Reglements_${filePrefix}_${formattedNow}.xlsx`);
};

export const generateCadenceExcel = (model, date) => {
    const { buckets, rows, totals } = model || { buckets: [], rows: [], totals: null };
    const headers = [
        "Survenus en",
        ...buckets.flatMap((b) => [`Payés ${b.label} - sinistres`, `Payés ${b.label} - règlements`, `Payés ${b.label} - mt`]),
        "TOTAL - sinistres", "TOTAL - règlements", "TOTAL - mt", "Déclarés", "Taux %",
    ];
    const body = rows.map((r) => ([
        r.bucket.label,
        ...buckets.flatMap((cb) => [r.cells[cb.key].nbSinistres, r.cells[cb.key].nbReglements, r.cells[cb.key].montant]),
        r.totalNbSinistres, r.totalNbReglements, r.totalMontant, r.declares,
        Number(r.taux.toFixed(1)),
    ]));
    const totalRow = totals ? [
        "TOTAL",
        ...buckets.flatMap((cb) => [totals.cells[cb.key].nbSinistres, totals.cells[cb.key].nbReglements, totals.cells[cb.key].montant]),
        totals.totalNbSinistres, totals.totalNbReglements, totals.totalMontant, totals.declares, Number(totals.taux.toFixed(1)),
    ] : null;

    const wsData = [
        ["CADENCE DE RÈGLEMENT DES SINISTRES"],
        ...etatCompagnieRows(), ...etatSinistreRows(),
        [cadencePeriode(date)],
        [""],
        headers,
        ...body,
        ...(totalRow ? [totalRow] : []),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cadence");
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `Cadence_Reglement_${formattedNow}.xlsx`);
};
